/* eslint-disable */
import { AutoFitType, HorizontalAlign, VerticalAlign } from '@src/slide/domain/ui-model/element-modeles/property/text/constants/text-style-type';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
// eslint-disable-next-line no-unused-vars
import ShapeModel from '@src/slide/domain/ui-model/element-modeles/shape-model';
import { calResize, getBaseIndexWithMode, transform } from '@src/slide/domain/ui-model/element-modeles/property/text/utils/resize';
import { TextDirectionType } from '@src/slide/domain/ui-model/element-modeles/property/text/constants/text-style-type';
import { MODES } from '@src/slide/domain/ui-model/element-modeles/property/frame/frame.interface';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import {IShapeModel} from "@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface";

export function isShapeAutoFit(element: IElementBaseModel) {
    return (element.type === UIModelType.SHAPE) && ((element as IShapeModel).textBodyStyle?.autoFitType === AutoFitType.SHAPE_AUTO_FIT);
}

export function isFontAutoFit(element: IElementBaseModel): boolean {
    return (element.type === UIModelType.SHAPE) && ((element as IShapeModel).textBodyStyle?.autoFitType === AutoFitType.FONT_AUTO_FIT);
}

/**
 * 对于设置了"根据文本调整形状大小"的元素，缩放元素和文本高度变化均会引起元素高度的变化
 * 这时需要根据变化的方向和偏移量重新调整元素的宽高和位置信息
 * 涉及到旋转角度，简单加减高度会导致元素 left 和 top 错误
 * @param element
 * @param style
 * @param displayHeight
 * @param mode
 */
 export function getTargetSizeAfterHeightChange(element: IShapeModel, style, displayHeight: number, mode?: string, textDirection?: string) {
    const direction = getHeightChangeDirection(element, mode, textDirection);
    let offsetHeight = style.height - displayHeight;
    let targetSize = style;

    if (direction === `${MODES.TOP}_${MODES.BOTTOM}`) {
        offsetHeight = offsetHeight / 2;
        direction.split('_').forEach((dir) => {
            const { offsetX, offsetY } = getHeightOffsetXY(element, dir, offsetHeight);
            targetSize = calculateTargetSize(targetSize, element, dir, offsetX, offsetY);
        });
    } else {
        const { offsetX, offsetY } = getHeightOffsetXY(element, direction, offsetHeight);
        targetSize = calculateTargetSize(targetSize, element, direction, offsetX, offsetY);
    }
    return targetSize;
}

/**
 * 对于设置了"根据文本调整形状大小"的元素，输入/删除文本会导致元素宽度的变化
 * 需要根据变化的方向和偏移量重新调整元素的宽高和位置信息
 * @param element
 * @param style
 * @param displayWidth
 */
export function getTargetSizeAfterWidthChange(element: IShapeModel, style, displayWidth: number, mode?: string, textDirection?: string) {
    const direction = getWidthChangeDirection(element, mode, textDirection);
    let offsetWidth = style.width - displayWidth;
    let targetSize = style;

    if (direction === `${MODES.LEFT}_${MODES.RIGHT}`) {
        offsetWidth = offsetWidth / 2;
        direction.split('_').forEach((dir) => {
            const { offsetX, offsetY } = getWidthOffsetXY(element, dir, offsetWidth);
            targetSize = calculateTargetSize(targetSize, element, dir, offsetX, offsetY);
        });
    } else {
        const { offsetX, offsetY } = getWidthOffsetXY(element, direction, offsetWidth);
        targetSize = calculateTargetSize(targetSize, element, direction, offsetX, offsetY);
    }
    return targetSize;
}

/**
 * 根据 direction 得到元素高度变化时的偏移量
 * @param element
 * @param direction [ TOP, BOTTOM, TOP_BOTTOM ]
 * @param offsetHeight
 */
function getHeightOffsetXY(element: IShapeModel, direction: string, offsetHeight: number) {
    const rotate = element.props.style?.rotate || 0;
    // 角度转弧度
    const angle = rotate * Math.PI / 180;
    const flag = (direction?.indexOf(MODES.TOP) === -1) ? 1 : -1;
    const offsetX = offsetHeight * Math.sin(angle) * flag;
    const offsetY = -offsetHeight * Math.cos(angle) * flag;
    return { offsetX, offsetY };
}

/**
 * 根据 direction 得到元素宽度变化时的偏移量
 * @param element
 * @param direction [ LEFT, RIGHT, LEFT_RIGHT ]
 * @param displayWidth
 */
function getWidthOffsetXY(element: IShapeModel, direction: string, displayWidth: number) {
    const rotate = element.props.style?.rotate || 0;
    // 角度转弧度
    const angle = rotate * Math.PI / 180;
    const flag = (direction?.indexOf(MODES.RIGHT) === -1) ? 1 : -1;
    const offsetX = displayWidth * Math.cos(angle) * flag;
    const offsetY = displayWidth * Math.sin(angle) * flag;
    return { offsetX, offsetY };
}

/**
 * 获取高度偏移的方向
 * 如果传入 mode，则根据 resize mode 调整，否则偏移方向依赖于元素的垂直对齐方式
 * @param element
 * @param mode 元素 resize mode
 */
function getHeightChangeDirection(element: IShapeModel, mode?: string, textDirection?: string) {
    let direction = MODES.BOTTOM;
    if (textDirection === TextDirectionType.ROTATE_270) {
        direction = MODES.TOP;
    }
    const alignItems = element?.textBodyStyle?.wrapperStyle?.alignItems;

    if (alignItems === VerticalAlign.CENTER) {
        direction = `${MODES.TOP}_${MODES.BOTTOM}`;
    } else if (alignItems === VerticalAlign.BOTTOM) {
        direction = MODES.TOP;
    }

    if (mode) {
        const tempDir = (mode?.indexOf(MODES.BOTTOM) !== -1) ? MODES.BOTTOM : direction;
        direction = (mode?.indexOf(MODES.TOP) !== -1) ? MODES.TOP : tempDir;
    }

    return direction;
}

/**
 * 获取宽度偏移的方向
 * @param element
 */
function getWidthChangeDirection(element: IShapeModel, mode?: string, textDirection?: string) {
    let direction = MODES.RIGHT;
    if (
        textDirection === TextDirectionType.VERTICAL ||
        textDirection === TextDirectionType.ROTATE_90 ||
        textDirection === TextDirectionType.WORD_ART_VERTICAL_RTL
    ) {
        direction = MODES.LEFT;
    }

    // 以首段的对齐方式为准
    const textAlign = element?.paragraphList?.[0]?.paragraphProps?.textAlign;

    if (textAlign === HorizontalAlign.CENTER || textAlign === HorizontalAlign.JUSTIFY) {
        direction = `${MODES.LEFT}_${MODES.RIGHT}`;
    } else if (textAlign === HorizontalAlign.RIGHT) {
        direction = MODES.LEFT;
    }

    if (mode) {
        // 传入mode即竖排拖拽场景, 方向需根据文本方向调整
        direction = textDirection === TextDirectionType.ROTATE_270 ? MODES.RIGHT : MODES.LEFT;
        const tempDir = (mode?.indexOf(MODES.LEFT) !== -1) ? MODES.LEFT : direction;
        direction = (mode?.indexOf(MODES.RIGHT) !== -1) ? MODES.RIGHT : tempDir;
    }
    return direction;
}

/**
 * 根据元素初始位置 style, 变化方向 direction, 以及偏移量 offsetX 和 offsetY, 计算元素偏移后的宽高和位置信息
 * @param style
 * @param element
 * @param direction
 * @param offsetX
 * @param offsetY
 */
function calculateTargetSize(style, element: IShapeModel, direction: string, offsetX: number, offsetY: number) {
    const elementInfo: any = {
        props: {
            style,
        },
    };
    const sizeAtStart = getSizeInfo(elementInfo);
    const flipAtStart = getFlipInfo(elementInfo);
    sizeAtStart.left = 0;
    sizeAtStart.top = 0;
    const {
        left, top, width, height,
    } = sizeAtStart;

    const rotate = element.props.style?.rotate || 0;
    // 计算初始状态旋转后的rect
    const transformedRect = transform({
        x: left,
        y: top,
        width,
        height,
    }, rotate);

    const baseIndex = getBaseIndexWithMode(direction, flipAtStart);

    const params = {
        transformedRect,
        flipAtStart,
        sizeAtStart,
        baseIndex,
        type: element.type,
        rotate,
        offsetX,
        offsetY,
        isCroping: false,
        e: {},
    };
    const { targetSize } = calResize(params);

    // /还原左上顶点位置
    targetSize.left = style.left + targetSize.left;
    targetSize.top = style.top + targetSize.top;

    return targetSize;
}

function getSizeInfo(elementModel: IElementBaseModel) {
    const elementStyle = elementModel.props.style;
    return {
        width: elementStyle.width,
        height: elementStyle.height,
        left: elementStyle.left,
        top: elementStyle.top,
        centerX: (elementStyle.width / 2) + elementStyle.left,
        centerY: (elementStyle.height / 2) + elementStyle.top,
    };
}

function getFlipInfo(elementModel: IElementBaseModel) {
    const elementStyle = elementModel.props.style;
    return {
        flipH: elementStyle.flipH === undefined ? 0 : elementStyle.flipH,
        flipV: elementStyle.flipV === undefined ? 0 : elementStyle.flipV,
    };
}

export const testObj = {
    getHeightOffsetXY,
    getWidthOffsetXY,
    getHeightChangeDirection,
    getWidthChangeDirection,
    calculateTargetSize,
};
